import { ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { cn } from '../lib/cn';

type Sig = Record<string, unknown>;

interface FieldDef {
  key: string;
  label: string;
  fmt?: (v: unknown, sig?: Sig) => string | { text: string; tone?: 'good' | 'bad' | 'warn' };
}

interface Group {
  title: string;
  fields: FieldDef[];
}

const yesNo = (v: unknown) => (v == null ? '-' : v ? '是' : '否');
const truthy = (good: boolean) => (v: unknown) => {
  if (v == null) return { text: '-' };
  const isTrue = Boolean(v);
  const ok = good ? isTrue : !isTrue;
  return { text: isTrue ? '是' : '否', tone: ok ? ('good' as const) : ('bad' as const) };
};
const number =
  (suffix = '') =>
  (v: unknown) =>
    v == null ? '-' : `${v}${suffix}`;
const hashFmt = (v: unknown) => {
  if (!v) return '-';
  const s = String(v);
  return s.length > 16 ? `${s.slice(0, 8)}…${s.slice(-6)}` : s;
};

const GROUPS: Group[] = [
  {
    title: '基础信息',
    fields: [
      { key: 'userAgent', label: 'User-Agent' },
      { key: 'platform', label: '平台' },
      { key: 'languages', label: '语言', fmt: (v) => (Array.isArray(v) ? v.join(', ') : '-') },
      { key: 'timezone', label: '时区' },
      {
        key: 'inAppBrowser',
        label: '内置浏览器 (in-app)',
        fmt: (v, s) => {
          const app = typeof v === 'string' ? v : '';
          if (app) return { text: app, tone: 'good' };
          // Android System WebView（UA 带 "; wv)"）是真实 in-app 环境,只是不带广告平台标识,
          // 与引擎 isSystemWebview 一致——规则已认它为 in-app,这里显示也对齐,不再笼统报「否」。
          const ua = typeof s?.userAgent === 'string' ? s.userAgent : '';
          if (/;\s*wv\)/i.test(ua)) return { text: '系统 WebView', tone: 'good' };
          return { text: '否', tone: 'warn' };
        },
      },
    ],
  },
  {
    title: '设备',
    fields: [
      { key: 'hardwareConcurrency', label: 'CPU 核心数' },
      { key: 'deviceMemory', label: '内存(GB)' },
      {
        key: 'screenWidth',
        label: '屏幕分辨率',
        fmt: (_, s?: Sig) => `${s?.screenWidth ?? '?'} × ${s?.screenHeight ?? '?'}` as never,
      },
      { key: 'devicePixelRatio', label: '像素密度 DPR' },
      { key: 'colorDepth', label: '色深' },
      { key: 'maxTouchPoints', label: '最大触点数' },
    ],
  },
  {
    title: '浏览器自动化迹象',
    fields: [
      {
        key: 'botdBot',
        label: 'BotD 判定',
        fmt: (v) => {
          if (v == null) return { text: '未判定 (fail-open)', tone: 'warn' };
          return v ? { text: '爬虫', tone: 'bad' } : { text: '真人', tone: 'good' };
        },
      },
      {
        key: 'botdKind',
        label: 'BotD 类型',
        fmt: (v) => (v ? { text: String(v), tone: 'bad' } : '-'),
      },
      {
        key: 'webdriver',
        label: 'navigator.webdriver',
        fmt: (v) => ({ text: v ? '是(异常)' : '否', tone: v ? 'bad' : 'good' }),
      },
      { key: 'pluginsCount', label: '插件数' },
      { key: 'hasChromeRuntime', label: 'window.chrome 存在', fmt: yesNo },
      { key: 'hasNotificationPermissionMismatch', label: '通知权限矛盾', fmt: yesNo },
      { key: 'platformUaMatch', label: 'Platform 与 UA 一致', fmt: truthy(true) },
      {
        key: 'perfNowJitter',
        label: 'performance.now 抖动',
        fmt: (v) => {
          if (v == null) return '-';
          const n = Number(v);
          return {
            text: n.toFixed(3),
            tone: n < 0.05 ? ('bad' as const) : ('good' as const),
          };
        },
      },
    ],
  },
  {
    title: '指纹哈希',
    fields: [
      { key: 'canvasHash', label: 'Canvas', fmt: hashFmt },
      { key: 'webglHash', label: 'WebGL', fmt: hashFmt },
      { key: 'audioHash', label: 'Audio', fmt: hashFmt },
      { key: 'fontsHash', label: 'Fonts', fmt: hashFmt },
      { key: 'webglRendererSummary', label: 'WebGL 渲染器' },
    ],
  },
  {
    title: '用户行为',
    fields: [
      { key: 'dwellMs', label: '停留时长', fmt: number('ms') },
      { key: 'mouseEvents', label: '鼠标事件数' },
      { key: 'touchEvents', label: '触摸事件数' },
      { key: 'scrollEvents', label: '滚动事件数' },
      { key: 'keyEvents', label: '键盘事件数' },
      { key: 'pointerType', label: '指针类型(mouse/touch/pen)' },
      { key: 'visibilityChanges', label: '页面可见性切换次数' },
    ],
  },
  {
    title: '运动传感器',
    fields: [
      {
        key: 'motionSamples',
        label: '加速度计采样数',
        fmt: (v) => ({
          text: String(v ?? 0),
          tone: Number(v ?? 0) > 0 ? ('good' as const) : ('warn' as const),
        }),
      },
      { key: 'motionMaxDelta', label: '最大加速度变化' },
      { key: 'orientationSamples', label: '陀螺仪采样数' },
    ],
  },
  {
    title: '移动设备特征',
    fields: [
      { key: 'hoverNone', label: '触屏媒体查询 hover:none', fmt: truthy(true) },
      { key: 'pointerCoarse', label: '触屏媒体查询 pointer:coarse', fmt: truthy(true) },
      { key: 'hasTouchEventCtor', label: 'ontouchstart 存在', fmt: truthy(true) },
      { key: 'hasVibrate', label: 'navigator.vibrate 存在', fmt: yesNo },
      { key: 'orientationType', label: '屏幕方向类型' },
      { key: 'orientationAngle', label: '屏幕方向角度' },
      { key: 'visualViewportScale', label: '可视区域缩放比' },
      { key: 'iosStandalone', label: 'iOS 主屏添加模式', fmt: yesNo },
    ],
  },
  {
    title: '电池',
    fields: [
      { key: 'hasBattery', label: '电池 API 存在', fmt: yesNo },
      { key: 'batteryCharging', label: '正在充电', fmt: yesNo },
      {
        key: 'batteryLevel',
        label: '电量',
        fmt: (v) => (v == null ? '-' : `${(Number(v) * 100).toFixed(0)}%`),
      },
    ],
  },
  {
    title: '网络连接',
    fields: [
      { key: 'connectionType', label: '连接类型' },
      { key: 'connectionDownlink', label: '下行带宽(Mbps)' },
      { key: 'connectionRtt', label: 'RTT(ms)' },
      { key: 'saveData', label: '省流量模式', fmt: yesNo },
    ],
  },
  {
    title: '媒体设备',
    fields: [
      { key: 'mediaInputs', label: '音频输入数' },
      { key: 'mediaOutputs', label: '音频输出数' },
      { key: 'mediaCameras', label: '摄像头数' },
    ],
  },
  {
    title: '存储能力',
    fields: [
      { key: 'hasLocalStorage', label: 'localStorage', fmt: truthy(true) },
      { key: 'hasSessionStorage', label: 'sessionStorage', fmt: truthy(true) },
      { key: 'hasIndexedDB', label: 'IndexedDB', fmt: truthy(true) },
    ],
  },
  {
    title: '主动挑战',
    fields: [
      {
        key: 'challengePassed',
        label: '挑战通过',
        fmt: (v) => ({ text: v ? '是' : '否', tone: v ? 'good' : 'bad' }),
      },
      { key: 'challengeKind', label: '挑战方式(click/touch/timeout/none)' },
      { key: 'challengeMs', label: '挑战耗时', fmt: number('ms') },
      { key: 'touchForce', label: '触摸压力 force' },
      { key: 'touchRadiusX', label: '触摸半径 X' },
      { key: 'touchRadiusY', label: '触摸半径 Y' },
    ],
  },
  {
    title: '自动化工具痕迹',
    fields: [
      {
        key: 'driverMarkers',
        label: 'window 上 driver 变量',
        fmt: (v) => {
          const arr = Array.isArray(v) ? v : [];
          if (arr.length === 0) return { text: '无', tone: 'good' as const };
          return { text: arr.join(', '), tone: 'bad' as const };
        },
      },
      {
        key: 'nativeFnTampered',
        label: '被改写的原生函数',
        fmt: (v) => {
          const arr = Array.isArray(v) ? v : [];
          if (arr.length === 0) return { text: '无', tone: 'good' as const };
          return { text: arr.join(', '), tone: 'bad' as const };
        },
      },
    ],
  },
  {
    title: '鼠标轨迹生物特征',
    fields: [
      { key: 'mouseSampleCount', label: '采样数' },
      {
        key: 'mouseAngleStdDev',
        label: '角度标准差 (弧度)',
        fmt: (v) => {
          if (v == null) return '-';
          const n = Number(v);
          return {
            text: n.toFixed(4),
            tone: n < 0.1 ? ('bad' as const) : ('good' as const),
          };
        },
      },
      { key: 'mouseSpeedStdDev', label: '速度标准差 (px/ms)' },
    ],
  },
  {
    title: 'Client Hints (来自请求头)',
    fields: [
      { key: 'clientHintUa', label: 'sec-ch-ua' },
      { key: 'clientHintMobile', label: 'sec-ch-ua-mobile' },
      { key: 'clientHintPlatform', label: 'sec-ch-ua-platform' },
    ],
  },
  {
    title: '高级能力',
    fields: [
      { key: 'webgpuVendor', label: 'WebGPU 厂商' },
      { key: 'webgpuArchitecture', label: 'WebGPU 架构' },
      {
        key: 'speechVoicesCount',
        label: 'TTS 语音数量',
        fmt: (v) => {
          if (v == null) return '-';
          const n = Number(v);
          return {
            text: String(n),
            tone: n === 0 ? ('warn' as const) : ('good' as const),
          };
        },
      },
      {
        key: 'hasPlatformAuthenticator',
        label: 'WebAuthn 平台认证器 (FaceID/指纹)',
        fmt: yesNo,
      },
      {
        key: 'googlePayStatus',
        label: 'Google Pay 探测状态',
        fmt: (v) => {
          if (v == null) return '-';
          const labels: Record<string, string> = {
            present: '有支付方式',
            absent: '无支付方式',
            rejected: 'API 拒绝',
            'sdk-unavailable': 'SDK 不可用',
            timeout: '探测超时',
          };
          return {
            text: labels[String(v)] ?? String(v),
            tone: v === 'present' ? ('good' as const) : ('warn' as const),
          };
        },
      },
      { key: 'googlePayError', label: 'Google Pay 错误' },
      {
        key: 'cssFeatures',
        label: 'CSS 现代特性',
        fmt: (v) => {
          if (!v || typeof v !== 'object') return '-';
          const entries = Object.entries(v as Record<string, boolean>);
          const supported = entries.filter(([, ok]) => ok).map(([k]) => k);
          return supported.length ? supported.join(', ') : '全不支持';
        },
      },
    ],
  },
  {
    title: 'v2: 帧率与 Canvas',
    fields: [
      {
        key: 'rafJitter',
        label: 'rAF 帧率抖动',
        fmt: (v) => {
          if (v == null) return '-';
          const n = Number(v);
          return {
            text: `${n.toFixed(3)}ms`,
            tone: n < 0.3 ? ('bad' as const) : ('good' as const),
          };
        },
      },
      {
        key: 'canvasStable',
        label: 'Canvas 渲染稳定性',
        fmt: (v) => {
          if (v == null) return '-';
          return {
            text: v ? '稳定' : '不一致(噪声)',
            tone: v ? ('good' as const) : ('bad' as const),
          };
        },
      },
    ],
  },
  {
    title: 'v2: 滚动与点击行为',
    fields: [
      { key: 'scrollSamples', label: '滚动采样数' },
      {
        key: 'scrollSpeedStdDev',
        label: '滚动速度标准差',
        fmt: (v) => {
          if (v == null) return '-';
          const n = Number(v);
          return { text: n.toFixed(3), tone: n < 0.5 ? ('warn' as const) : ('good' as const) };
        },
      },
      { key: 'clickSamples', label: '点击采样数' },
      {
        key: 'clickPositionStdDev',
        label: '点击位置标准差 (px)',
        fmt: (v) => {
          if (v == null) return '-';
          const n = Number(v);
          return { text: n.toFixed(1), tone: n < 5 ? ('bad' as const) : ('good' as const) };
        },
      },
    ],
  },
  {
    title: 'v2: 权限与能力',
    fields: [
      { key: 'permissionCamera', label: '相机权限' },
      { key: 'permissionMicrophone', label: '麦克风权限' },
      { key: 'permissionGeolocation', label: '地理位置权限' },
      { key: 'hasClipboard', label: 'Clipboard API', fmt: yesNo },
      { key: 'pdfViewerEnabled', label: 'PDF 查看器', fmt: yesNo },
      { key: 'mimeTypesCount', label: 'MIME 类型数' },
    ],
  },
  {
    title: 'v2: Probe 交叉验证',
    fields: [
      { key: 'probeCountry', label: 'Probe 检测到的国家' },
      {
        key: 'probeTimezoneMatch',
        label: '时区与国家匹配',
        fmt: (v) => {
          if (v == null) return '-';
          return { text: v ? '匹配' : '不匹配', tone: v ? ('good' as const) : ('bad' as const) };
        },
      },
      {
        key: 'probeLanguageMatch',
        label: '语言与国家匹配',
        fmt: (v) => {
          if (v == null) return '-';
          return { text: v ? '匹配' : '不匹配', tone: v ? ('good' as const) : ('bad' as const) };
        },
      },
    ],
  },
];

export function SignalsView({ signals }: { signals: Sig }) {
  const [showRaw, setShowRaw] = useState(false);
  return (
    <div className="space-y-4">
      {GROUPS.map((g) => {
        const visible = g.fields.filter(({ key, label }) => {
          if (label.includes('Screen') && key === 'screenWidth') return true;
          return signals[key] !== undefined;
        });
        if (visible.length === 0) return null;
        return (
          <div key={g.title}>
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-zinc-500">
              {g.title}
            </div>
            <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {visible.map(({ key, label, fmt }) => {
                const raw = signals[key];
                let display: string;
                let tone: 'good' | 'bad' | 'warn' | undefined;
                if (fmt) {
                  // some fmt fns ignore the second arg
                  const result = (fmt as (v: unknown, s?: Sig) => unknown)(raw, signals);
                  if (typeof result === 'object' && result && 'text' in result) {
                    display = (result as { text: string }).text;
                    tone = (result as { tone?: typeof tone }).tone;
                  } else {
                    display = String(result ?? '-');
                  }
                } else {
                  display = raw == null || raw === '' ? '-' : String(raw);
                }
                return (
                  <div
                    key={key}
                    className="flex items-baseline justify-between gap-3 border-b border-zinc-100 py-1 text-sm dark:border-zinc-800/60"
                  >
                    <span className="shrink-0 text-zinc-500">{label}</span>
                    <span
                      className={cn(
                        'truncate text-right font-mono text-xs',
                        tone === 'good' && 'text-emerald-600 dark:text-emerald-400',
                        tone === 'bad' && 'text-red-600 dark:text-red-400',
                        tone === 'warn' && 'text-amber-600 dark:text-amber-400',
                      )}
                      title={display}
                    >
                      {display}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <button
        type="button"
        onClick={() => setShowRaw((s) => !s)}
        className="flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-800 dark:hover:text-zinc-200"
      >
        <ChevronRight className={cn('h-3.5 w-3.5 transition-transform', showRaw && 'rotate-90')} />
        原始 JSON
      </button>
      {showRaw && (
        <pre className="max-h-96 overflow-auto rounded-md bg-zinc-50 p-3 text-xs dark:bg-zinc-950">
          {JSON.stringify(signals, null, 2)}
        </pre>
      )}
    </div>
  );
}
